// Suggests codes for new expense categories (XXX) and sub-categories (XXX-YYY), matching the
// backend validation: category /^[A-Z]{3}$/, sub-category /^[A-Z]{3}-[A-Z]{3}$/. Codes are unique.

export const CATEGORY_CODE_PATTERN = /^[A-Z]{3}$/;
export const SUB_CATEGORY_CODE_PATTERN = /^[A-Z]{3}-[A-Z]{3}$/;

const lettersOnly = (value) => String(value || '').toUpperCase().replace(/[^A-Z]/g, '');
const words = (value) => String(value || '').toUpperCase().split(/[^A-Z]+/).filter(Boolean);

// Ordered three-letter candidates for a name, e.g. "OFFICE SUPPLIES" -> OFF, OSU, OFS, ...
const threeLetterCandidates = (name) => {
  const letters = lettersOnly(name);
  if (!letters) return [];

  const candidates = [];
  const parts = words(name);
  const consonants = letters[0] + letters.slice(1).replace(/[AEIOU]/g, '');

  if (letters.length >= 3) candidates.push(letters.slice(0, 3));
  if (parts.length >= 3) candidates.push(parts.slice(0, 3).map((w) => w[0]).join(''));
  if (parts.length === 2) {
    candidates.push(parts[0][0] + parts[1].slice(0, 2));
    candidates.push(parts[0].slice(0, 2) + parts[1][0]);
  }
  if (consonants.length >= 3) candidates.push(consonants.slice(0, 3));
  for (let i = 1; i < letters.length - 1; i += 1) {
    for (let j = i + 1; j < letters.length; j += 1) {
      candidates.push(letters[0] + letters[i] + letters[j]);
    }
  }
  if (letters.length < 3) candidates.push(letters.padEnd(3, 'X'));

  return [...new Set(candidates.filter((c) => CATEGORY_CODE_PATTERN.test(c)))];
};

const pickUnused = (candidates, used) => {
  const taken = new Set((used || []).map((c) => String(c || '').toUpperCase()));
  return candidates.find((c) => !taken.has(c)) || candidates[0] || '';
};

export const suggestCategoryCode = (name, existingCodes = []) =>
  pickUnused(threeLetterCandidates(name), existingCodes);

// Prefix is the parent category's own code; if it has none, it is derived from the category name.
export const suggestSubCategoryCode = (category, name, existingCodes = []) => {
  const prefix = CATEGORY_CODE_PATTERN.test(String(category?.code || ''))
    ? category.code
    : threeLetterCandidates(category?.category_name || category?.name)[0];
  if (!prefix) return '';

  const suffix = pickUnused(
    threeLetterCandidates(name),
    (existingCodes || [])
      .map((c) => String(c || '').toUpperCase())
      .filter((c) => c.startsWith(`${prefix}-`))
      .map((c) => c.slice(prefix.length + 1))
  );
  return suffix ? `${prefix}-${suffix}` : '';
};
